import { ExtendedArticleEntityType, ExtendedTariffEntityType } from "./types";
import { JobStatusEnum } from "@/lib/types/job";
import {
	JobItemEntityTypeInsert,
	JobWithShare,
	TariffEntityType,
} from "@/lib/supabase/supabaseTypes";
import { JobItemTypeEnum } from "@/lib/supabase/supabaseEnums";
import { JobStoreUnion, useJobStore } from ".";
import { v4 as uuidv4 } from "uuid";
import { showNotification } from "../Central/selectors";
import { TpTier, TpValue } from "../../types/enums";
import { Logger } from "@/lib/logger/Logger";
import { JobDocumentTypeEnum } from "../../pages/job-page/job-document/job-document.types";
import { JobStage } from "./job-list.store";

const tpTierName = {
	0: "tp_sv",
	1: "tp_pp1",
	2: "tp_pp2",
	3: "tp_pp3",
};

export const calculateTariffPrice = (
	tariffData: ExtendedTariffEntityType | TariffEntityType,
	jobTpTier: TpTier,
	jobTpVariation: number
): number => {
	// Get base tp points based on job's tp_tier
	const tierKey = tpTierName[jobTpTier] as keyof typeof tariffData;
	let tp = (tariffData[tierKey] as number) ?? (tariffData.tp_pp2 as number);
	
	// Apply tp_variation if not 0
	if (jobTpVariation && jobTpVariation !== 0) {
		tp = tp + (tp * jobTpVariation) / 100;
		tp = Math.round(tp * 20) / 20; // Round to nearest 0.05
	}
	
	return tp;
};

export const formatTariff = (
	item: ExtendedTariffEntityType,
	jobDocumentId: number,
	clientId: string,
	guarantorId: string | null,
	jobTpTier: TpTier,
	jobTpValue: TpValue,
	jobTpVariation: number
): JobItemEntityTypeInsert | null => {
	Logger.info(`[formatTariff] item: ${JSON.stringify(item)}`);
	const {
		id,
		code,
		code_e,
		description_de,
		description_fr,
		description_it,
		is_new,
		own_tp,
		is_own_tp,
		composition,
		standard_quantity,
		tarifwerk,
		tp_pp1,
		tp_pp2,
		tp_pp3,
		tp_sv,
		custom,
		description_other,
		organization_id,
		tariff_default_id,
		tar_id,
		...filteredItem
	} = item;

	const taxpunktwert = is_own_tp ? own_tp : jobTpValue;
	if (taxpunktwert === null) {
		showNotification({
			message: "Taxpunktwert ist null",
			type: "error",
		});
		return null;
	}

	// Warn the user if is_own_tp is true but own_tp is 0
	if (taxpunktwert === 0) {
		showNotification({
			message: `${is_own_tp ? "Eigener " : ""}Taxpunktwert ist 0`,
			type: "warning",
		});
	}

	// Calculate the tariff price using the helper function
	const tp = calculateTariffPrice(item, jobTpTier, jobTpVariation);

	Logger.info(`[formatTariff] tp: ${tp}`);

	const newRow: JobItemEntityTypeInsert = {
		...filteredItem,
		id: uuidv4(),
		created_at: undefined,
		modified_at: new Date().toISOString(),
		price: tp,
		quantity: standard_quantity ?? 1,
		description: description_de, // TODO: localize
		code, // for display on documents
		code_e, // for display in the jobs table
		tp_value: taxpunktwert,
		job_document_id: jobDocumentId,
		tariff_id: item?.custom ? item?.id : null, // If the tariff is custom, we use the id from the custom tariffs table
		type: JobItemTypeEnum.TARIFF,
		// TS was complaining about types needed explicit assertion
		tax_rate: (useJobStore.getState().job?.tax_rate as number) ?? 8.1,
	};

	return newRow;
};

export const formatArticle = (
	item: ExtendedArticleEntityType,
	jobDocumentId: number
): JobItemEntityTypeInsert => {
	Logger.info(`[formatArticle] item: ${JSON.stringify(item)}`);

	// Filter out properties that are not needed in the job item
	const {
		id,
		code_e, // for articles we only have code_e because there's no need for an official code
		description_de,
		description_fr,
		description_it,
		is_new,
		standard_quantity,
		article_default_id,
		custom,
		description_other,
		manufacturer,
		margin,
		modified_stock_quantity_at,
		organization_id,
		purchase_price,
		purchase_quantity,
		stock_quantity,
		unit,
		art_id,
		...filteredItem
	} = item;

	const newRow: JobItemEntityTypeInsert = {
		...filteredItem,
		id: uuidv4(),
		created_at: undefined,
		modified_at: new Date().toISOString(),
		quantity: standard_quantity ?? 1,
		description: description_de, // TODO: localize
		code: code_e,
		code_e: code_e,
		job_document_id: jobDocumentId,
		price: item.price ?? 0,
		article_id: item.custom ? item.id : null, // If the article is custom, we use the id from the custom articles table
		tax_rate: (useJobStore.getState().job?.tax_rate as number) ?? 8.1,
		tp_value: 0, // Articles do not have a TP value
		type: item.type ?? JobItemTypeEnum.ARTICLE_MATERIAL,
	};

	return newRow;
};

export const adjustJobStatus = (
	state: JobStoreUnion,
	jobId: number,
	targetStatus: JobStatusEnum,
	currentStatus: JobStatusEnum
) => {
	let jobIndex = -1;
	let stageIndex: JobStage | null = null;

	if (state.jobList[currentStatus].stages) {
		// Search through all stages to find the job
		for (const stage of Object.keys(state.jobList[currentStatus].data)) {
			const stageData =
				state.jobList[currentStatus].data[stage as JobStage];
			const foundJobIndex = stageData.jobs.findIndex(
				(j: JobWithShare) => j.id === jobId
			);

			if (foundJobIndex !== -1) {
				stageIndex = stage as JobStage;
				jobIndex = foundJobIndex;
				break;
			}
		}
	} else {
		jobIndex = state.jobList[currentStatus].data.jobs.findIndex(
			(j: JobWithShare) => j.id === jobId
		);
	}

	// If the job is not in the job list, the state is not updated
	if (jobIndex === -1) return;

	const job = state.jobList[currentStatus].stages
		? state.jobList[currentStatus].data[stageIndex as JobStage].jobs[
				jobIndex
			]
		: state.jobList[currentStatus].data.jobs[jobIndex];

	// If the job to be updated is the current job, update it as well
	if (state.job?.id === jobId) {
		state.job.status = targetStatus;
	}

	const stage = getJobStage(job);

	// Remove the job from the list of its current status
	if (state.jobList[currentStatus].stages) {
		state.jobList[currentStatus].data[stageIndex as JobStage].jobCount -= 1;
	} else {
		state.jobList[currentStatus].data.jobCount -= 1;
	}

	if (state.jobList[currentStatus].stages) {
		state.jobList[currentStatus].data[stageIndex as JobStage].jobs.splice(
			jobIndex,
			1
		);
	} else {
		state.jobList[currentStatus].data.jobs.splice(jobIndex, 1);
	}

	// Add the job to the list of its new status
	if (state.jobList[targetStatus].stages) {
		state.jobList[targetStatus].data[stage].jobCount += 1;
		state.jobList[targetStatus].data[stage].jobs.unshift(job);
	} else {
		state.jobList[targetStatus].data.jobCount += 1;
		state.jobList[targetStatus].data.jobs.unshift(job);
	}
};

export const jobDocumentTypeNamePresets = (type: JobDocumentTypeEnum) => {
	let name = "Neues Dokument";
	if (type === JobDocumentTypeEnum.DeliveryNote) {
		name = "Lieferschein";
	} else if (type === JobDocumentTypeEnum.Quotation) {
		name = "Kostenvoranschlag";
	} else if (type === JobDocumentTypeEnum.CreditNote) {
		name = "Gutschrift";
	} else if (type === JobDocumentTypeEnum.Tracing) {
		name = "Rückverfolgung";
	}
	return name;
};

export const isUpdateJobCodeAllowed = (jobStatus: JobStatusEnum) => {
	if (
		jobStatus === JobStatusEnum.COMPLETED ||
		jobStatus === JobStatusEnum.BOOKED_MONTHLY ||
		jobStatus === JobStatusEnum.BOOKED_SINGLE
	) {
		return {
			isAllowed: false,
			explanation: "Der Auftrag ist abgeschlossen oder gebucht",
		};
	}

	return {
		isAllowed: true,
		needsConfirmation: false,
		explanation: "Der Auftrag ist nicht abgeschlossen oder gebucht",
	};
};

export const getJobListEntry = (jobs: JobWithShare[], stage: JobStage) => {
	const categories = [
		JobDocumentTypeEnum.Quotation,
		JobDocumentTypeEnum.DeliveryNote,
		JobDocumentTypeEnum.CreditNote,
	];

	if (!jobs) return { jobCount: 0, jobs: [] };

	switch (stage) {
		case "empty":
			const emptyJobs = jobs.filter(
				(j) =>
					!j.job_document_types ||
					!j.job_document_types.some((t) =>
						categories.includes(t as JobDocumentTypeEnum)
					)
			);
			return { jobCount: emptyJobs.length, jobs: emptyJobs };
		case JobDocumentTypeEnum.Quotation:
			const quotationJobs = jobs.filter(
				(j) =>
					j.job_document_types &&
					j.job_document_types.includes(
						JobDocumentTypeEnum.Quotation
					) &&
					!j.job_document_types.includes(
						JobDocumentTypeEnum.DeliveryNote
					) &&
					!j.job_document_types.includes(
						JobDocumentTypeEnum.CreditNote
					)
			);
			return { jobCount: quotationJobs.length, jobs: quotationJobs };
		case JobDocumentTypeEnum.DeliveryNote:
			const deliveryNoteJobs = jobs.filter(
				(j) =>
					j.job_document_types &&
					j.job_document_types.includes(
						JobDocumentTypeEnum.DeliveryNote
					) &&
					!j.job_document_types.includes(
						JobDocumentTypeEnum.CreditNote
					)
			);
			return {
				jobCount: deliveryNoteJobs.length,
				jobs: deliveryNoteJobs,
			};
		case JobDocumentTypeEnum.CreditNote:
			const creditNoteJobs = jobs.filter(
				(j) =>
					j.job_document_types &&
					j.job_document_types.includes(
						JobDocumentTypeEnum.CreditNote
					)
			);
			return { jobCount: creditNoteJobs.length, jobs: creditNoteJobs };
		default:
			return { jobCount: jobs.length, jobs: jobs };
	}
};

export const getJobStage = (job: JobWithShare) => {
	if (
		!job.job_document_types ||
		(!job.job_document_types.includes(JobDocumentTypeEnum.Quotation) &&
			!job.job_document_types.includes(
				JobDocumentTypeEnum.DeliveryNote
			) &&
			!job.job_document_types.includes(JobDocumentTypeEnum.CreditNote))
	)
		return "empty";
	if (
		job.job_document_types.includes(JobDocumentTypeEnum.Quotation) &&
		!job.job_document_types.includes(JobDocumentTypeEnum.DeliveryNote) &&
		!job.job_document_types.includes(JobDocumentTypeEnum.CreditNote)
	)
		return JobDocumentTypeEnum.Quotation;
	if (
		job.job_document_types.includes(JobDocumentTypeEnum.DeliveryNote) &&
		!job.job_document_types.includes(JobDocumentTypeEnum.CreditNote)
	)
		return JobDocumentTypeEnum.DeliveryNote;
	if (job.job_document_types.includes(JobDocumentTypeEnum.CreditNote))
		return JobDocumentTypeEnum.CreditNote;
	return "empty";
};

export const getStatusAndStageFromId = (jobId: number) => {
	const jobList = useJobStore.getState().jobList;
	let foundStatus: JobStatusEnum | null = null;
	let foundStage: JobStage | null = null;

	for (const status of Object.keys(jobList) as unknown as JobStatusEnum[]) {
		if (jobList[status].stages) {
			for (const stage of Object.keys(
				jobList[status].data
			) as unknown as JobStage[]) {
				const jobs = jobList[status].data[stage].jobs;
				const jobIndex = jobs.findIndex((j) => j.id === jobId);
				if (jobIndex !== -1) {
					foundStatus = status as JobStatusEnum;
					foundStage = stage as JobStage;
					break;
				}
			}
		} else {
			const jobs = jobList[status].data.jobs;
			const jobIndex = jobs.findIndex((j) => j.id === jobId);
			if (jobIndex !== -1) {
				foundStatus = status as JobStatusEnum;
				foundStage = null;
				break;
			}
		}
	}

	return { status: foundStatus, stage: foundStage };
};
